import { updateTimesheetEntry, updateUsersLatestReviewedDate } from "../api";

import { TIMESHEET_QUERY_KEYS } from "../../query-keys";
import { invalidateTimesheetQueries } from "components/timesheet/queries";
import { queryClient } from "utils/react-query";
import { selectUser } from "store/src/slices/auth-slice";
import { toast } from "sonner";
// mutations/useUpdateTimesheetEntry.js
import { useMutation } from "@tanstack/react-query";
import { useSelector } from "react-redux";

export function useUpdateTimesheetEntry(options = {}) {
  const { onSuccess: customOnSuccess, onError: customOnError } = options;

  const user = useSelector(selectUser);

  return useMutation({
    mutationFn: async ({ id, values }) => {
      return updateTimesheetEntry({
        id,
        item: values,
      });
    },
    onMutate: async ({ id, values }) => {
      const date = values.date;
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({
        queryKey: [TIMESHEET_QUERY_KEYS.TIMESHEET_REVIEW, date],
      });

      // Snapshot the previous value
      const previousTimesheet = queryClient.getQueryData([
        TIMESHEET_QUERY_KEYS.TIMESHEET_REVIEW,
        date,
      ]);

      // Optimistically update to the new value
      queryClient.setQueryData(
        [TIMESHEET_QUERY_KEYS.TIMESHEET_REVIEW, date],
        (old) => {
          return old
            ? old.map((entry) =>
                entry.id === id ? { ...entry, ...values } : entry
              )
            : [];
        }
      );

      // Return a context object with the snapshotted value
      return { previousTimesheet, date, id };
    },
    onError: (error, variables, context) => {
      // If the mutation fails, use the context returned from onMutate to roll back
      if (context?.previousTimesheet) {
        queryClient.setQueryData(
          [TIMESHEET_QUERY_KEYS.TIMESHEET_REVIEW, context.date],
          context.previousTimesheet
        );
      }

      customOnError(error);
    },
    onSuccess: async (data, variables, context) => {
      // Update with the actual response data
      if (user?.id) {
        try {
          await updateUsersLatestReviewedDate({ userId: user.id });
        } catch (e) {
          console.error("Failed to update user's latest reviewed date", e);
          toast.warning("Updated timesheet, but failed to log review date.");
        }
      }

      queryClient.setQueryData(
        [TIMESHEET_QUERY_KEYS.TIMESHEET_REVIEW, context.date],
        (old) => old?.map((e) => (e.id === context.id ? data : e))
      );

      invalidateTimesheetQueries(context.date);

      customOnSuccess();
    },
  });
}
